import { ICalculatorRequest, ICalculatorResponse } from "models/calculator";
import { novaStars } from "models/novastar";

import { gcd, toDecimalsAsNumber } from "./";
import { IQuoteLineItems } from "models/quote";
import { IMount, mounts, MountType } from "models/mounts";

export const calculator = (req: ICalculatorRequest): ICalculatorResponse => {
    let markup = 2;
    if (req.markup && req.markup > 0) {
        markup = req.markup;
    }

    if (!req.items) {
        req.items = [];
    }

    const { cabinet, width, height } = req;
    const requestedWidthInMillimeters = width * 0.3048 * 1000;
    const requestedHeightInMillimeters = height * 0.3048 * 1000;

    let numberOfCabinetsWide = Math.round(requestedWidthInMillimeters / (cabinet.module.width * cabinet.modules.width));
    let numberOfCabinetsTall = Math.round(requestedHeightInMillimeters / (cabinet.module.height * cabinet.modules.height));

    if (cabinet.restrictions.layout === 'equal') {
        if (numberOfCabinetsWide > numberOfCabinetsTall) {
            numberOfCabinetsWide = numberOfCabinetsTall;
        } else {
            numberOfCabinetsTall = numberOfCabinetsWide;
        }
    }

    const weightLbs = cabinet.weight * numberOfCabinetsWide * numberOfCabinetsTall * 2.20462;
    const actualWidthMeters = numberOfCabinetsWide * cabinet.module.width * cabinet.modules.width / 1000;
    const actualHeightMeters = numberOfCabinetsTall * cabinet.module.height * cabinet.modules.height / 1000;
    const actualWidthFt = (actualWidthMeters * 3.28084).toFixed(2);
    const actualHeightFt = (actualHeightMeters * 3.28084).toFixed(2);
    const actualSquareMeters = toDecimalsAsNumber(actualWidthMeters * actualHeightMeters, 2);

    const resolutionWidth = cabinet.module.resolution.width * cabinet.modules.width * numberOfCabinetsWide;
    const resolutionHeight = cabinet.module.resolution.height * cabinet.modules.height * numberOfCabinetsTall;
    const physicalPixels = resolutionWidth * resolutionHeight;

    const novastar = novaStars.find((n) => physicalPixels <= n.max.pixels);

    let r = -1;
    if (resolutionWidth && resolutionHeight) {
        r = gcd(resolutionWidth, resolutionHeight);
    }

    const maxPowerWatts = cabinet.maxPower * numberOfCabinetsWide * numberOfCabinetsTall;
    const maxPowerAmps = maxPowerWatts / 110;
    const twentyAmps = Math.ceil(maxPowerAmps / 20);
    const fifteenAmps = Math.ceil(maxPowerAmps / 15);

    const aspectRatio = r > 0 ? `${resolutionWidth / r}:${resolutionHeight / r}` : 'N/A';

    //#region the mounting hareware
    let mountingHardware: IMount | undefined;
    switch (req.mountType) {
        case MountType.Wall:
            mountingHardware = mounts.get(MountType.Wall);
            break;
        case MountType.Flybars:
            mountingHardware = mounts.get(MountType.Flybars);
            break;
        case MountType.GroundStack:
            mountingHardware = mounts.get(MountType.GroundStack);
            break;
    }

    let mountingHardwareCost = 0;
    let mountingHardwareQuantity = 0;
    let mountingHardwareTotalCost = 0;
    if (mountingHardware) {
        switch (mountingHardware.priceUnit) {
            case 'per-square-meter':
                mountingHardwareQuantity = toDecimalsAsNumber(Math.ceil(actualSquareMeters), 2);
                mountingHardwareCost = mountingHardware.cost;
                break;
            case 'per-unit':
                mountingHardwareQuantity = toDecimalsAsNumber(Math.ceil(numberOfCabinetsWide), 2);
                mountingHardwareCost = mountingHardware.cost;
                break;
        }
    }
    mountingHardwareTotalCost = mountingHardwareCost * mountingHardwareQuantity;
    //#endregion

    if (req.includeSpares) {
        const spareModules = Math.ceil((numberOfCabinetsWide * numberOfCabinetsTall * (cabinet.modules.height * cabinet.modules.width)) * 0.1);
        req.items.splice(0, 0, {
            description: "Spare Modules",
            quantity: spareModules,
            unitPrice: toDecimalsAsNumber(cabinet.module.cost * markup, 2),
            total: toDecimalsAsNumber(spareModules * cabinet.module.cost * markup, 2),
        });

        const sparePowerSupply = Math.ceil((numberOfCabinetsWide * numberOfCabinetsTall) * 0.13);
        req.items.splice(1, 0, {
            description: "Spare Power Supply",
            quantity: sparePowerSupply,
            unitPrice: toDecimalsAsNumber(52 * markup, 2),
            total: toDecimalsAsNumber(sparePowerSupply * 52 * markup, 2),
        });

        const spareReceivingCard = Math.ceil((numberOfCabinetsWide * numberOfCabinetsTall) * 0.1);
        req.items.splice(2, 0, {
            description: "Spare Receiving Card",
            quantity: spareReceivingCard,
            unitPrice: toDecimalsAsNumber(20 * markup, 2),
            total: toDecimalsAsNumber(spareReceivingCard * 20 * markup, 2),
        });
    }

    let cabinetsCost = parseFloat(cabinet.cost) * numberOfCabinetsWide * numberOfCabinetsTall;
    if (cabinet.costUnit === 'per-square-meter') {
        console.log('square meters:', actualSquareMeters);
        cabinetsCost = parseFloat(cabinet.cost) * actualSquareMeters;
    }

    const cabinetsPrice = cabinetsCost * markup;
    let originalPrice = (cabinetsCost * markup) + (novastar?.price || 0) + (mountingHardwareTotalCost * markup) + req.items.reduce((acc, i) => acc + (i.quantity * i.unitPrice), 0);
    let price = (cabinetsCost * markup) + (novastar?.price || 0) + (mountingHardwareTotalCost * markup) + req.items.reduce((acc, i) => acc + (i.quantity * i.unitPrice), 0);

    if (req.includeTool) {
        originalPrice += 480;
        price += 480;
    }

    let shippingCrates = 0;
    let shippingUnitCost = 60 * markup;
    if (req.includeShippingPallets && cabinet.restrictions.packaging) {
        shippingCrates = Math.ceil((numberOfCabinetsWide * numberOfCabinetsTall) / cabinet.restrictions.packaging) + 2;

        originalPrice += shippingCrates * shippingUnitCost;
        price += shippingCrates * shippingUnitCost;
    }

    if (req.ourTotalCost && req.ourTotalCost > 0) {
        console.log(req.ourTotalCost);
        originalPrice = req.ourTotalCost * markup;
        price = req.ourTotalCost * markup;
    }

    console.log(originalPrice, price);

    if (req.discount?.amount && req.discount?.amount > 0) {
        price -= req.discount.amount;
    }

    if (req.discount?.percent && req.discount?.percent > 0) {
        price -= price * req.discount.percent / 100;
    }

    let discountedAmount = 0;
    if (price < originalPrice) {
        discountedAmount = originalPrice - price;
    }

    const nf = new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const nf0 = new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const items: IQuoteLineItems[] = [
        {
            description: cabinet.invoiceDescription || cabinet.name,
            quantity: numberOfCabinetsWide * numberOfCabinetsTall,
            unitPrice: toDecimalsAsNumber(cabinetsPrice / (numberOfCabinetsWide * numberOfCabinetsTall), 2),
            total: cabinetsPrice,
        },
        {
            description: 'NovaStar Processor',
            quantity: 1,
            unitPrice: novastar?.price || 0,
            total: novastar?.price || 0,
        },
    ];

    if (mountingHardware || mountingHardwareQuantity > 0) {
        items.push({
            description: mountingHardware?.name || 'Mounting Hardware',
            quantity: toDecimalsAsNumber(mountingHardwareQuantity, 2),
            unitPrice: mountingHardwareCost * markup,
            total: (mountingHardwareCost * markup) * mountingHardwareQuantity,
        });
    }

    if (req.includeTool) {
        items.push({
            description: 'Maintenance Tool',
            quantity: 1,
            unitPrice: 480,
            total: 480,
        });
    }

    if (req.includeShippingPallets) {
        items.push({
            description: 'Shipping Crates',
            quantity: shippingCrates,
            unitPrice: shippingUnitCost,
            total: shippingCrates * shippingUnitCost,
        });
    }

    if (req.items) {
        req.items.forEach((i) => {
            items.push({
                description: i.description,
                quantity: i.quantity,
                unitPrice: i.unitPrice,
                total: i.quantity * i.unitPrice,
            });
        });
    }

    return {
        dimensionsInMeters: {
            width: nf.format(actualWidthMeters * 1000),
            height: nf.format(actualHeightMeters * 1000),
        },
        squareMeters: nf.format(actualSquareMeters),
        dimensionsInFeet: {
            width: actualWidthFt,
            height: actualHeightFt,
        },
        squareFeet: nf.format(width * height),
        cabinets: {
            width: nf0.format(numberOfCabinetsWide),
            height: nf0.format(numberOfCabinetsTall),
        },
        totalCabinets: numberOfCabinetsWide * numberOfCabinetsTall,
        resolution: {
            width: nf0.format(resolutionWidth),
            height: nf0.format(resolutionHeight),
        },
        resolutionFormatted: `${resolutionWidth} x ${resolutionHeight}`,
        pixels: nf0.format(physicalPixels),
        aspectRatio: {
            width: (resolutionWidth / r).toFixed(2),
            height: (resolutionHeight / r).toFixed(2),
        },
        aspectRatioFormatted: aspectRatio,
        cost: nf.format(cabinetsCost),
        cabinetsPrice: nf.format(cabinetsPrice),
        originalPrice: nf.format(originalPrice),
        price: nf.format(price),
        cabinet,
        cabinetPrice: nf.format(parseFloat(cabinet.cost) * 2),
        novastar,
        discount: req.discount,
        discountAmount: discountedAmount > 0 ? nf.format(discountedAmount) : undefined,
        weight: {
            kg: nf.format(cabinet.weight * numberOfCabinetsWide * numberOfCabinetsTall),
            lb: nf.format(Math.ceil(weightLbs)),
        },
        power: {
            watts: nf0.format(maxPowerWatts),
            amps: {
                total: nf.format(maxPowerAmps),
                twenty: nf.format(twentyAmps),
                fifteen: nf.format(fifteenAmps),
            },
        },
        mount: mountingHardware,
        mountPrice: nf.format((mountingHardwareCost * markup) * mountingHardwareQuantity),
        items,
    };
};
